import type { OpDraft, OpKind, OpPayload, Shape, ShapeGeometry, SyncOp } from "./shapes";

/**
 * opLog - the annotation op log: idempotent application, deterministic replay, reconciliation
 * with the server's seq order, and per-author undo/redo as inverse ops.
 *
 * Divergence is repaired by merging the backend's room_state ops, never by peer negotiation
 * (D-09). Undo is per author through inverse ops appended to the shared log, derived from the
 * log itself rather than a separate undo stack (D-10).
 */

export const REMOTE_PENDING_TTL_MS = 10_000;

type PendingEntry = { op: SyncOp; receivedAtMs: number; local: boolean };

export type OpLogState = {
  confirmed: ReadonlyMap<string, SyncOp>;
  pending: ReadonlyMap<string, PendingEntry>;
  maxConfirmedSeq: number;
};

export function emptyOpLog(): OpLogState {
  return { confirmed: new Map(), pending: new Map(), maxConfirmedSeq: -1 };
}

function isKnown(s: OpLogState, opId: string): boolean {
  return s.confirmed.has(opId) || s.pending.has(opId);
}

function withPending(s: OpLogState, op: SyncOp, nowMs: number, local: boolean): OpLogState {
  if (isKnown(s, op.opId)) {
    return s;
  }
  const pending = new Map(s.pending);
  pending.set(op.opId, { op, receivedAtMs: nowMs, local });
  return { ...s, pending };
}

export function addLocal(s: OpLogState, op: SyncOp, nowMs: number): OpLogState {
  return withPending(s, op, nowMs, true);
}

export function applyRemote(s: OpLogState, op: SyncOp, nowMs: number): OpLogState {
  return withPending(s, op, nowMs, false);
}

export function confirmLocal(s: OpLogState, opId: string, seq: number): OpLogState {
  const entry = s.pending.get(opId);
  if (!entry) {
    return s;
  }
  const pending = new Map(s.pending);
  pending.delete(opId);
  const confirmed = new Map(s.confirmed);
  confirmed.set(opId, { ...entry.op, seq });
  return { confirmed, pending, maxConfirmedSeq: Math.max(s.maxConfirmedSeq, seq) };
}

export function rejectLocal(s: OpLogState, opId: string): OpLogState {
  if (!s.pending.has(opId)) {
    return s;
  }
  const pending = new Map(s.pending);
  pending.delete(opId);
  return { ...s, pending };
}

export function mergeServerOps(
  s: OpLogState,
  ops: SyncOp[],
  nowMs: number,
  ttlMs: number = REMOTE_PENDING_TTL_MS,
): OpLogState {
  const confirmed = new Map(s.confirmed);
  const pending = new Map(s.pending);
  let maxConfirmedSeq = s.maxConfirmedSeq;

  for (const incoming of ops) {
    if (incoming.seq === null) {
      continue;
    }
    pending.delete(incoming.opId);
    confirmed.set(incoming.opId, incoming);
    maxConfirmedSeq = Math.max(maxConfirmedSeq, incoming.seq);
  }

  const mergedIds = new Set(ops.map((o) => o.opId));
  for (const [opId, entry] of pending) {
    if (!entry.local && !mergedIds.has(opId) && nowMs - entry.receivedAtMs > ttlMs) {
      pending.delete(opId);
    }
  }

  return { confirmed, pending, maxConfirmedSeq };
}

function findOp(s: OpLogState, opId: string): SyncOp | undefined {
  return s.confirmed.get(opId) ?? s.pending.get(opId)?.op;
}

export function orderedOps(s: OpLogState, frameId: string): SyncOp[] {
  const confirmedOps = Array.from(s.confirmed.values())
    .filter((o) => o.frameId === frameId)
    .sort((a, b) => (a.seq! - b.seq!) || a.opId.localeCompare(b.opId));

  const pendingOps = Array.from(s.pending.values())
    .filter((e) => e.op.frameId === frameId)
    .sort((a, b) => (a.receivedAtMs - b.receivedAtMs) || a.op.opId.localeCompare(b.op.opId))
    .map((e) => e.op);

  return [...confirmedOps, ...pendingOps];
}

type ReplayShape = { shape: Shape; deleted: boolean };

function replayFrame(s: OpLogState, frameId: string): { order: string[]; byId: Map<string, ReplayShape> } {
  const byId = new Map<string, ReplayShape>();
  const order: string[] = [];

  for (const op of orderedOps(s, frameId)) {
    switch (op.kind) {
      case "ShapeAdd": {
        const payload = op.payload as { shape?: ShapeGeometry & { id?: string; tool?: string } };
        const shape = payload?.shape;
        if (!shape || typeof shape.id !== "string") {
          break;
        }
        const existing = byId.get(shape.id);
        if (existing && !existing.deleted) {
          // A live shape already occupies this id: a duplicate ShapeAdd is a no-op.
          break;
        }
        const revivedShape: Shape = {
          id: shape.id,
          tool: shape.tool as Shape["tool"],
          authorIdentity: op.authorIdentity,
          points: shape.points,
          color: shape.color,
          width: shape.width,
        };
        if (existing) {
          // Tombstoned by a prior ShapeDelete: a re-issued ShapeAdd (e.g. redo) revives it
          // in its original creation-order slot rather than appending a new one.
          existing.shape = revivedShape;
          existing.deleted = false;
        } else {
          byId.set(shape.id, { shape: revivedShape, deleted: false });
          order.push(shape.id);
        }
        break;
      }
      case "ShapeUpdate": {
        const payload = op.payload as { shapeId?: string; after?: ShapeGeometry };
        const existing = payload?.shapeId ? byId.get(payload.shapeId) : undefined;
        if (!existing || !payload.after) {
          break;
        }
        existing.shape = {
          ...existing.shape,
          points: payload.after.points,
          color: payload.after.color,
          width: payload.after.width,
        };
        break;
      }
      case "ShapeDelete": {
        const payload = op.payload as { shapeIds?: string[] };
        for (const id of payload?.shapeIds ?? []) {
          const existing = byId.get(id);
          if (existing) {
            existing.deleted = true;
          }
        }
        break;
      }
      case "ShapeRestore": {
        const payload = op.payload as { shapeIds?: string[] };
        for (const id of payload?.shapeIds ?? []) {
          const existing = byId.get(id);
          if (existing) {
            existing.deleted = false;
          }
        }
        break;
      }
      default:
        break;
    }
  }

  return { order, byId };
}

export function renderFrame(s: OpLogState, frameId: string): Shape[] {
  const { order, byId } = replayFrame(s, frameId);
  const shapes: Shape[] = [];
  for (const id of order) {
    const entry = byId.get(id);
    if (entry && !entry.deleted) {
      shapes.push(entry.shape);
    }
  }
  return shapes;
}

export function liveShapeIds(s: OpLogState, frameId: string): string[] {
  return renderFrame(s, frameId).map((shape) => shape.id);
}

export function inverseOf(op: { kind: OpKind; payload: OpPayload }): { kind: OpKind; payload: OpPayload } {
  switch (op.kind) {
    case "ShapeAdd": {
      const payload = op.payload as { shape: ShapeGeometry & { id: string } };
      return { kind: "ShapeDelete", payload: { shapeIds: [payload.shape.id] } };
    }
    case "ShapeDelete": {
      const payload = op.payload as { shapeIds: string[] };
      return { kind: "ShapeRestore", payload: { shapeIds: [...payload.shapeIds] } };
    }
    case "ShapeRestore": {
      const payload = op.payload as { shapeIds: string[] };
      return { kind: "ShapeDelete", payload: { shapeIds: [...payload.shapeIds] } };
    }
    case "ShapeUpdate": {
      const payload = op.payload as { shapeId: string; before: ShapeGeometry; after: ShapeGeometry };
      return {
        kind: "ShapeUpdate",
        payload: { shapeId: payload.shapeId, before: payload.after, after: payload.before },
      };
    }
    default:
      return op;
  }
}

export function authorHistory(
  s: OpLogState,
  frameId: string,
  identity: string,
): { undo: string[]; redo: string[] } {
  const undo: string[] = [];
  const redo: string[] = [];

  for (const op of orderedOps(s, frameId)) {
    if (op.authorIdentity !== identity) {
      continue;
    }
    if (op.undoOf) {
      const idx = undo.lastIndexOf(op.undoOf);
      if (idx !== -1) {
        undo.splice(idx, 1);
      }
      redo.push(op.undoOf);
    } else if (op.redoOf) {
      const idx = redo.lastIndexOf(op.redoOf);
      if (idx !== -1) {
        redo.splice(idx, 1);
      }
      undo.push(op.opId);
    } else {
      undo.push(op.opId);
      redo.length = 0;
    }
  }

  return { undo, redo };
}

export function makeUndo(s: OpLogState, frameId: string, identity: string): OpDraft | null {
  const { undo } = authorHistory(s, frameId, identity);
  const top = undo[undo.length - 1];
  if (!top) {
    return null;
  }
  const target = findOp(s, top);
  if (!target) {
    return null;
  }
  const inverse = inverseOf(target);
  return { frameId, kind: inverse.kind, payload: inverse.payload, undoOf: top, redoOf: null };
}

export function makeRedo(s: OpLogState, frameId: string, identity: string): OpDraft | null {
  const { redo } = authorHistory(s, frameId, identity);
  const top = redo[redo.length - 1];
  if (!top) {
    return null;
  }
  const target = findOp(s, top);
  if (!target) {
    return null;
  }
  return { frameId, kind: target.kind, payload: target.payload, undoOf: null, redoOf: top };
}
